import { inject } from '@angular/core';
import type { ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot } from '@angular/router';
import { catchError, of } from 'rxjs';

import { AuthService } from '@auth/repository';
import {
  getUserSelectedCompany,
  hasApplicationRole,
  hasServiceSync,
} from '@auth/repository/auth.repository';
import type { TermAndCondition } from '@core/models/app.model';
import { Utils } from '@core/models/utils';
import { NotifyService } from '@core/services/notifyService.service';
import { UME_SERVICE } from '@core/services/ume-services';
import { APP_ROLE } from '@shared/constants/ume-constants';

import { ProviderService } from '../../../provider/services/provider.service';
import type {
  ICompanyServiceOffering,
  IServiceOfferingConfiguration,
  IServiceOfferingMetadata,
  ITACInfo,
} from '../models/services.model';
import { ServicesManagementService } from '../services/services-configuration.service';

export const servicesResolver: ResolveFn<IServiceOfferingConfiguration[]> = () => {
  return inject(ServicesManagementService)
    .getServices(null)
    .pipe(catchError(() => of([])));
};

export const companyServicesResolver: ResolveFn<ICompanyServiceOffering[]> = () => {
  const hasAccessToServicesConfig =
    hasServiceSync(UME_SERVICE.SERVICE_MANAGE_SUBSCRIPTIONS) &&
    hasApplicationRole(APP_ROLE.MY_ACCOUNT_SERVICES);
  return hasAccessToServicesConfig
    ? inject(ServicesManagementService)
        .getCompanyServices()
        .pipe(catchError(() => of([])))
    : of([]);
};

export const termsInfoResolver: ResolveFn<ITACInfo | null> = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const offeringId = Utils.parentRouteParam(state.root, 'offeringId');
  return offeringId
    ? inject(ProviderService)
        .getTermsAndConditions(offeringId)
        .pipe(
          catchError(() => {
            inject(NotifyService).notifyErrorOnly('Failed to load terms and conditions');
            return of(null);
          })
        )
    : of(null);
};

export const companyTermsInfoResolver: ResolveFn<TermAndCondition[]> = () => {
  const companyId: string = getUserSelectedCompany().guid;
  return companyId
    ? inject(AuthService)
        .getCompanyApplicableTerms(companyId)
        .pipe(
          catchError(() => {
            inject(NotifyService).notifyErrorOnly('Failed to load terms and conditions');
            return of([]);
          })
        )
    : of([]);
};

export const currentServiceResolver: ResolveFn<IServiceOfferingMetadata | null> = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const offeringId = Utils.parentRouteParam(state.root, 'offeringId');
  return offeringId
    ? inject(ServicesManagementService)
        .getService(offeringId)
        .pipe(
          catchError(() => {
            inject(NotifyService).notifyErrorOnly('Failed to load service settings');
            return of(null);
          })
        )
    : of(null);
};

export const companyCurrentServiceResolver: ResolveFn<ICompanyServiceOffering | null> = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const offeringId = Utils.parentRouteParam(state.root, 'offeringId');
  return offeringId
    ? inject(ServicesManagementService)
        .getCompanyService(offeringId)
        .pipe(
          catchError(() => {
            inject(NotifyService).notifyErrorOnly('Failed to load service settings');
            return of(null);
          })
        )
    : of(null);
};

export const serviceTermsAndConditionsResolver: ResolveFn<TermAndCondition[]> = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const offeringId = Utils.parentRouteParam(state.root, 'offeringId');
  return offeringId
    ? inject(ServicesManagementService)
        .getServiceTermsAndConditions(offeringId)
        .pipe(
          catchError(() => {
            inject(NotifyService).notifyErrorOnly('Failed to load service terms and conditions');
            return of([]);
          })
        )
    : of([]);
};
